import { computed, inject, type Signal } from '@angular/core';

import {
  legacyPropsToBlockConfig,
  type ResolveBlockConfigRuntimeInput,
} from '@front-platform/content-builder';
import { BLOCK_CONFIG_CONTEXT } from '@core/services/content-builder/block-config-context.token';
import {
  resolveBlockConfigColors,
  type BlockConfigWithCssColors,
} from '@shared/utils/content-builder/resolve-block-config-colors';

export type ComputedResolvedConfigSource<
  Config extends Record<string, unknown>,
  Extension extends object = Record<never, never>,
> = () => {
  /** Legacy block-factory camelCase props; converted to snake_case config keys. */
  legacyConfig?: object;
  config?: Partial<Omit<Config, keyof Extension> & Extension>;
};

export type ResolvedConfigWithExtension<
  Config extends Record<string, unknown>,
  Extension extends object,
> = keyof Extension extends never ? Config : Omit<Config, keyof Extension> & Partial<Extension>;

/**
 * Wraps a block `resolveConfig` from `doodBlockConfig` into an Angular `computed`.
 *
 * Must be called from an Angular injection context (e.g. component field initializer)
 * so `BLOCK_CONFIG_CONTEXT` can be injected.
 */
export function computedResolvedConfig<
  Config extends Record<string, unknown>,
  Extension extends object = Record<never, never>,
>(
  resolveConfig: (input: ResolveBlockConfigRuntimeInput<Config>) => Config,
  source: ComputedResolvedConfigSource<Config, NoInfer<Extension>>,
): Signal<
  BlockConfigWithCssColors<ResolvedConfigWithExtension<Config, Extension>> & Record<string, unknown>
> {
  const blockContext = inject(BLOCK_CONFIG_CONTEXT);

  return computed(() => {
    const input = source();

    return resolveBlockConfigColors(
      resolveConfig({
        legacyConfig: input.legacyConfig
          ? legacyPropsToBlockConfig<Config>(input.legacyConfig)
          : {},
        config: (input.config ?? {}) as Partial<Config>,
        context: blockContext(),
      }),
    ) as BlockConfigWithCssColors<ResolvedConfigWithExtension<Config, Extension>> &
      Record<string, unknown>;
  });
}
